import ConfigService from "@shared/helpers/configServices"
import { useUserStore } from "@/stores/user-store"
import { useGlobalStore } from "@/stores/global-store"
import { PublicClientApplication, type Configuration } from "@azure/msal-browser"
import axios from "axios"
import { handleError } from "@shared/helpers/utils"
import { warningMessage } from "@shared/helpers/message"

export async function setup() {
  const authenticationPlugin: Configuration = {
    auth: {
      clientId: ConfigService.Current.openIdClientId,
      authority: ConfigService.Current.openIdAuthority,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  }

  const msalInstance = new PublicClientApplication(authenticationPlugin)
  await msalInstance.initialize()
  login(msalInstance)
  return msalInstance
}

export function login(msalInstance: PublicClientApplication) {
  const loginRequest = {
    scopes: ["openid profile email User.Read", ConfigService.Current.apiScope],
    redirectUri: window.location.origin,
  }
  const store = useUserStore()
  const globalStore = useGlobalStore()

  if (store.isAuthenticated) {
    return
  }

  msalInstance
    .handleRedirectPromise()
    .then(async (value) => {
      if (value) {
        globalStore.showAppLoading()
        msalInstance.setActiveAccount(value.account)
        const token = await getToken("api", msalInstance)

        // enrich
        const tokenClaims = value.idTokenClaims as { employeeid?: string; email?: string }
        const employeeId = tokenClaims["employeeid"]?.toString()
        const email = tokenClaims["email"]?.toString()
        axios
          .post(
            ConfigService.Current.apis.base + "/Authentication/Enrich",
            { externalId: employeeId ?? null, email, token: value.idToken },
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .then((result) => {
            if (result.status === 200) {
              const userModel = result.data.result
              userModel.accessToken = token
              store.setUser(userModel)
              if (!store.isRegisteredInJtp) {
                warningMessage("profile.messages.notRegistered")
              }
            }
            globalStore.hideAppLoading()
          })
          .catch(() => {
            handleError("general.messages.failedLoadPermissions")
            globalStore.hideAppLoading()
          })
      } else {
        msalInstance.loginRedirect(loginRequest)
      }
    })
    .catch((error) => {
      console.error(error)
    })
}

export async function logout(msalInstance: PublicClientApplication) {
  const store = useUserStore()
  store.logoutUser()

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  const claims = account?.idTokenClaims as { login_hint?: string } | undefined
  const logoutHint = claims?.login_hint ?? account?.username

  await msalInstance.logoutRedirect({
    account,
    logoutHint,
    postLogoutRedirectUri: window.location.origin,
  })
}

export async function getToken(type: string, msal: PublicClientApplication): Promise<string | null> {
  const scopes = type === "api" ? [ConfigService.Current.apiScope] : []
  const result = await msal.acquireTokenSilent({ scopes })
  return result.accessToken
}
